import { useEffect, useState } from 'react';
import JsonViewComponent from '@uiw/react-json-view';

import { Modal } from '..';
import Button from '../Button';
import { onCopy } from '../Copy';
import { useModal } from '../../hooks';
import type { ButtonProps } from '../Button';

type JsonViewProps = {
  value: object;
};

export const JsonView: React.FC<JsonViewProps> = ({ value }) => {
  return (
    <JsonViewComponent
      value={value}
      displayDataTypes={false}
      displayObjectSize={false}
      onCopied={onCopy}
    />
  );
};

type JsonEditViewProps = {
  value: string;
  onChange: (value: string) => void;
  onCancel: () => void;
};

export const JsonEditView: React.FC<JsonEditViewProps> = ({
  value,
  onChange,
  onCancel,
}) => {
  const [rawJson, setRawJson] = useState(value);
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    try {
      JSON.parse(rawJson);
      onChange(rawJson);
    } catch (e) {
      setError(
        e instanceof Error ? `Invalid JSON: ${e.message}` : 'Invalid JSON'
      );
    }
  };

  return (
    <div className="flex flex-col h-full gap-y-sm">
      <textarea
        className="w-full flex-1 min-h-[60vh] font-mono text-small p-md rounded-md border border-gray-300 bg-gray-50 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        value={rawJson}
        onChange={e => {
          setRawJson(e.target.value);
          setError(null);
        }}
        spellCheck={false}
      />
      <div className="flex items-center justify-between">
        {error && (
          <span className="text-red-600 text-small pl-sm">{error}</span>
        )}
        <div className="flex-1" />
        <div className="flex gap-x-sm">
          <Button type="light" label="Cancel" onClick={onCancel} />
          <Button label="Save" onClick={handleSave} />
        </div>
      </div>
    </div>
  );
};

type JsonViewModalProps = Omit<ButtonProps, 'label'> & {
  value: object;
  label: string;
  editable?: boolean;
  onChange?: (value: string) => void;
};

export const JsonViewModal: React.FC<JsonViewModalProps> = ({
  value,
  label,
  editable,
  onChange,
  ...rest
}) => {
  const { isOpened, onOpen, onClose } = useModal();
  const [isEditing, setIsEditing] = useState(false);
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const handleOpen = () => {
    setIsEditing(false);
    onOpen();
  };

  const handleChange = (rawJson: string) => {
    setDisplayValue(JSON.parse(rawJson));
    onChange?.(rawJson);
    setIsEditing(false);
  };

  const headerActions = editable && !isEditing && (
    <Button
      key="edit"
      type="light"
      label="Edit"
      onClick={() => setIsEditing(true)}
    />
  );

  return (
    <>
      <Button
        type="contrast"
        size="small"
        {...rest}
        label={label}
        onClick={handleOpen}
      />
      <Modal
        open={isOpened}
        onClose={onClose}
        title={label}
        headerActions={headerActions}
      >
        {isEditing ? (
          <JsonEditView
            value={JSON.stringify(displayValue, null, 2)}
            onChange={handleChange}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <JsonView key={JSON.stringify(displayValue)} value={displayValue} />
        )}
      </Modal>
    </>
  );
};
